import React, { useState, useEffect, useMemo } from 'react';
import { Button, FormGroup, Label, Input, Row, Col, FormFeedback, Alert } from 'reactstrap';
import { H5 } from '../../../../AbstractElements';
import { fetchFormData, createExpertReport, patchAccidentFileInsuranceApplication } from '../../../../Api/ApiMethods';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { getFieldAccess } from '../../../../_helper/fieldAccess';
import { INSURANCE_APPLICATION_TYPES } from '../../../../_helper/schemaAllowedValues';

const InsuranceCompanyReportForm = ({ formData, errors = {}, setFormData, fieldPermissions, canEdit = true, callFormState }) => {
    const { id } = useParams();
    const [reportTemplates, setReportTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedFileNumber, setSelectedFileNumber] = useState('');
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [creatingReport, setCreatingReport] = useState(false);
    const [petitionErrors, setPetitionErrors] = useState({});

    const [insuranceApplicationData, setInsuranceApplicationData] = useState({});
    const [insuranceApplicationErrors, setInsuranceApplicationErrors] = useState({});
    const [savingApplicationData, setSavingApplicationData] = useState({});

    const fileTemplateApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + "/filetemplate";
    const createReportApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + "/Report/create-report";

    const reapplicationStates = ['Yeni Eksper İşlem Bekleniyor', 'Yeni Eksper Rapor Bekleniyor', 'Yeni Eksper Raporu Ödeme Bekleniyor', 'Yeni Sigorta Başvuru Dilekçe Bekleniyor', 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor'];
    const insuranceOnlyState = 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor';
    const isInsuranceOnlyWorkflow = callFormState === insuranceOnlyState;
    const isReapplicationWorkflow = reapplicationStates.includes(callFormState);
    const requiresReapplicationFlag = isReapplicationWorkflow && !isInsuranceOnlyWorkflow;

    useEffect(() => {
        if (formData.filesToBeWorked && formData.filesToBeWorked.length > 0) {
            const initialData = {};
            formData.filesToBeWorked.forEach((accidentFileEntry) => {
                const accidentFileId = accidentFileEntry.accidentFileId;
                if (accidentFileId && (accidentFileEntry.insuranceApplicationType || accidentFileEntry.insuranceApplicationDate)) {
                    initialData[accidentFileId] = {
                        insuranceApplicationType: accidentFileEntry.insuranceApplicationType || '',
                        insuranceApplicationDate: accidentFileEntry.insuranceApplicationDate ? 
                            formatDateTimeForInput(accidentFileEntry.insuranceApplicationDate) : ''
                    };
                }
            });
            
            if (Object.keys(initialData).length > 0) {
                setInsuranceApplicationData(prev => ({
                    ...prev,
                    ...initialData
                }));
            }
        }
    }, [formData.filesToBeWorked]);

    useEffect(() => {
        const fetchReportTemplates = async () => {
            setLoadingTemplates(true);
            try {
                const response = await fetchFormData({
                    apiEndpoint: fileTemplateApiUrl,
                    page: 1,
                    pageSize: 100,
                    search: null,
                });

                console.log('Report templates API response:', response);

                let templates = [];
                if (response && response.isSuccess && response.data && Array.isArray(response.data.items)) {
                    templates = response.data.items.filter(template => template.isExpertReport === false);
                } else if (response && Array.isArray(response.items)) {
                    templates = response.items.filter(template => template.isExpertReport === false);
                } else if (Array.isArray(response)) {
                    templates = response.filter(template => template.isExpertReport === false);
                }

                setReportTemplates(templates);
                console.log('Sigorta dilekçe şablonları yüklendi:', templates);
            } catch (error) {
                console.error('Error fetching report templates:', error);
                toast.error('Rapor şablonları yüklenirken bir hata oluştu.');
                setReportTemplates([]);
            } finally {
                setLoadingTemplates(false);
            }
        };

        fetchReportTemplates();
    }, [fileTemplateApiUrl]);

    

    const isLorFileType = (title) => {
        if (!title) return false;
        const lowerTitle = title.toLowerCase();
        return lowerTitle.includes('hak mahrumiyeti') ||
               lowerTitle.includes('lor') ||
               lowerTitle.includes('mahrumiyeti');
    };

    const formatDateTimeForInput = (dateTimeString) => {
        if (!dateTimeString) return '';
        try {
            const date = new Date(dateTimeString);
            if (isNaN(date.getTime())) return '';
            
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    const handleInsuranceApplicationChange = (accidentFileId, field, value) => {
        setInsuranceApplicationData(prev => ({
            ...prev,
            [accidentFileId]: {
                ...prev[accidentFileId],
                [field]: value
            }
        }));
        if (insuranceApplicationErrors[accidentFileId]?.[field]) {
            setInsuranceApplicationErrors(prev => ({
                ...prev,
                [accidentFileId]: {
                    ...(prev[accidentFileId] || {}),
                    [field]: undefined
                }
            }));
        }
    };

    const handleSaveInsuranceApplication = async (accidentFileId) => {
        const applicationData = insuranceApplicationData[accidentFileId];
        const nextErrors = {};
        
        if (!applicationData?.insuranceApplicationDate) {
            nextErrors.insuranceApplicationDate = 'Sigorta başvuru tarihi zorunludur.';
        }
        
        if (!applicationData?.insuranceApplicationType) {
            nextErrors.insuranceApplicationType = 'Sigorta başvuru türü zorunludur.';
        }

        if (Object.keys(nextErrors).length > 0) {
            setInsuranceApplicationErrors(prev => ({
                ...prev,
                [accidentFileId]: nextErrors
            }));
            toast.error('Lütfen zorunlu sigorta başvuru bilgilerini doldurun.');
            return;
        }

        setSavingApplicationData(prev => ({ ...prev, [accidentFileId]: true }));
        
        try {
            const dateObject = new Date(applicationData.insuranceApplicationDate);
            const isoString = dateObject.toISOString();
            
            console.log('Saving insurance application data for accident file:', accidentFileId);
            
            await patchAccidentFileInsuranceApplication({
                accidentFileId: accidentFileId,
                insuranceApplicationType: applicationData.insuranceApplicationType,
                insuranceApplicationDate: isoString,
                readyForArbitration: true
            });
            
            toast.success('Sigorta başvuru bilgileri başarıyla kaydedildi.');
            
            setFormData(prevFormData => {
                const updatedFilesToBeWorked = prevFormData.filesToBeWorked.map(fileEntry => {
                    if (fileEntry.accidentFileId === accidentFileId) {
                        return {
                            ...fileEntry,
                            insuranceApplicationType: applicationData.insuranceApplicationType,
                            insuranceApplicationDate: isoString,
                            readyForArbitration: true
                        };
                    }
                    return fileEntry;
                });
                
                return {
                    ...prevFormData,
                    filesToBeWorked: updatedFilesToBeWorked
                };
            });
            
        } catch (error) {
            console.error('Error saving insurance application:', error);
        } finally {
            setSavingApplicationData(prev => ({ ...prev, [accidentFileId]: false }));
        }
    };

    const availableFileOptions = useMemo(() => {
        if (!formData.filesToBeWorked || formData.filesToBeWorked.length === 0) {
            return [];
        }

        return formData.filesToBeWorked.map((accidentFileEntry, index) => {
            const matchedAccidentFile = accidentFileEntry.accidentFileId
                ? formData.accidentFiles?.find(af => af.id === accidentFileEntry.accidentFileId)
                : null;

            const fileNumber = accidentFileEntry.fileNumber || (
                matchedAccidentFile ? matchedAccidentFile.fileNumber : (accidentFileEntry.accidentFileId || index.toString())
            );

            let allFileTypeTitles = [];

            if (accidentFileEntry.fileType && !accidentFileEntry.filesToBeWorked) {
                allFileTypeTitles = accidentFileEntry._fileTypeTitles || [];
            } else {
                accidentFileEntry.filesToBeWorked?.forEach(file => {
                    if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                        allFileTypeTitles.push(...file._fileTypeTitles);
                    }
                });
            }

            const hasNonLorTypes = allFileTypeTitles.some(title => !isLorFileType(title));

            if (!hasNonLorTypes && allFileTypeTitles.length > 0) {
                return null;
            }

            const isReapplicationFile = accidentFileEntry.isReapplicationFile === true || matchedAccidentFile?.isReapplicationFile === true;
            const isFromInsurance = accidentFileEntry.isFromInsurance === true || matchedAccidentFile?.isFromInsurance === true;

            if (isInsuranceOnlyWorkflow && !isFromInsurance) {
                return null;
            }

            if (requiresReapplicationFlag && !isReapplicationFile) {
                return null;
            }

            return {
                value: accidentFileEntry.accidentFileId || index.toString(),
                label: fileNumber,
                accidentFileId: accidentFileEntry.accidentFileId,
                fileTypeTitles: allFileTypeTitles,
                isReapplicationFile: isReapplicationFile || false,
                isFromInsurance: isFromInsurance || false,
            };
        }).filter(item => item !== null);
    }, [formData.filesToBeWorked, formData.accidentFiles, isInsuranceOnlyWorkflow, requiresReapplicationFlag]);

    useEffect(() => {
        if (selectedFileNumber && !availableFileOptions.some(option => option.value === selectedFileNumber)) {
            setSelectedFileNumber('');
        }
    }, [availableFileOptions, selectedFileNumber]);

    const generatePetitionAccess = getFieldAccess(fieldPermissions, 'insurancecompanyreport.generate', canEdit);

    const handleCreateReport = async () => {
        const nextErrors = {};

        if (!selectedFileNumber) {
            nextErrors.selectedFileNumber = 'Dosya no zorunludur.';
        }

        if (!selectedTemplate) {
            nextErrors.selectedTemplate = 'Dilekçe türü zorunludur.';
        }

        if (Object.keys(nextErrors).length > 0) {
            setPetitionErrors(nextErrors);
            toast.error('Lütfen zorunlu dilekçe bilgilerini doldurun.');
            return;
        }

        if (!id) {
            toast.error('Çağrı formu ID bulunamadı.');
            return;
        }

        const selectedOption = availableFileOptions.find(option => option.value === selectedFileNumber);
        if (!selectedOption) {
            toast.error('Seçilen dosya mevcut değil veya düzenlemeye kapalı.');
            setSelectedFileNumber('');
            return;
        }

        setCreatingReport(true);
        try {
            await createExpertReport({
                apiEndpoint: createReportApiUrl,
                callFormId: id,
                reportTemplateId: selectedTemplate,
                accidentFileId: selectedFileNumber
            });
            
            setSelectedTemplate('');
            setSelectedFileNumber('');
        } catch (error) {
            console.error('Error creating petition:', error);
        } finally {
            setCreatingReport(false);
        }
    };

    return (
        <>
            <H5>Sigorta Şirketi Dilekçeleri</H5>
            <div style={{ height: "20px" }}></div>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label for="fileNumberSelect">Dosya No <span className="text-danger">*</span></Label>
                        <Input
                            id="fileNumberSelect"
                            type="select"
                            value={selectedFileNumber}
                            onChange={(e) => {
                                setSelectedFileNumber(e.target.value);
                                setPetitionErrors(prev => ({ ...prev, selectedFileNumber: undefined }));
                            }}
                            disabled={!canEdit || generatePetitionAccess.disabled || availableFileOptions.length === 0}
                            invalid={!!petitionErrors.selectedFileNumber}
                        >
                            <option value="">Dosya numarası seçiniz...</option>
                            {availableFileOptions.map((fileOption) => (
                                <option key={fileOption.value} value={fileOption.value}>
                                    {fileOption.label} ({fileOption.fileTypeTitles.join(', ')})
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{petitionErrors.selectedFileNumber}</FormFeedback>
                        {availableFileOptions.length === 0 && (
                            <small className="text-muted">Henüz dilekçe oluşturulabilecek dosya bulunmamaktadır. (Sadece Hak Mahrumiyeti içeren dosyalar için dilekçe oluşturulamaz.)</small>
                        )}
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label for="templateSelect">Dilekçe Türü <span className="text-danger">*</span></Label>
                        <Input
                            id="templateSelect"
                            type="select"
                            value={selectedTemplate}
                            onChange={(e) => {
                                setSelectedTemplate(e.target.value);
                                setPetitionErrors(prev => ({ ...prev, selectedTemplate: undefined }));
                            }}
                            disabled={loadingTemplates || !canEdit || generatePetitionAccess.disabled}
                            invalid={!!petitionErrors.selectedTemplate}
                        >
                            <option value="">Dilekçe türü seçiniz...</option>
                            {reportTemplates.map((template) => (
                                <option key={template.id} value={template.id}>
                                    {template.title}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{petitionErrors.selectedTemplate}</FormFeedback>
                        {loadingTemplates && (
                            <small className="text-muted">Dilekçe şablonları yükleniyor...</small>
                        )}
                        {!loadingTemplates && reportTemplates.length === 0 && (
                            <small className="text-muted">Henüz dilekçe şablonu bulunmamaktadır.</small>
                        )}
                    </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                    <FormGroup className="mb-0">
                        <Button 
                            color="primary" 
                            onClick={handleCreateReport}
                            disabled={creatingReport || loadingTemplates || !canEdit || generatePetitionAccess.disabled}
                        >
                            {creatingReport ? 'Oluşturuluyor...' : 'Dilekçe Oluştur'}
                        </Button>
                    </FormGroup>
                </Col>
            </Row>

            <div style={{ height: "40px" }}></div>
            <H5>Sigorta Başvuru Bilgileri</H5>
            <div style={{ height: "20px" }}></div>
            {(errors.insuranceApplicationDate || errors.insuranceApplicationType || errors.insuranceApplicationFiles) && (
                <Alert color="danger" className="py-2">
                    {errors.insuranceApplicationDate || errors.insuranceApplicationType || errors.insuranceApplicationFiles}
                </Alert>
            )}
            
            {formData.filesToBeWorked && formData.filesToBeWorked.length > 0 ? (
                (() => {
                    const filteredFiles = formData.filesToBeWorked.filter((accidentFileEntry) => {
                        let allFileTypeTitles = [];
                        
                        if (accidentFileEntry.fileType && !accidentFileEntry.filesToBeWorked) {
                            allFileTypeTitles = accidentFileEntry._fileTypeTitles || [];
                        } else {
                            accidentFileEntry.filesToBeWorked?.forEach(file => {
                                if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                                    allFileTypeTitles.push(...file._fileTypeTitles);
                                }
                            });
                        }

                        const matchedAccidentFile = accidentFileEntry.accidentFileId
                            ? formData.accidentFiles?.find(af => af.id === accidentFileEntry.accidentFileId)
                            : null;
                        const isReapplicationFile = accidentFileEntry.isReapplicationFile === true || matchedAccidentFile?.isReapplicationFile === true;
                        const isFromInsurance = accidentFileEntry.isFromInsurance === true || matchedAccidentFile?.isFromInsurance === true;

                        if (isInsuranceOnlyWorkflow && !isFromInsurance) {
                            return false;
                        }

                        if (requiresReapplicationFlag && !isReapplicationFile) {
                            return false;
                        }

                        const hasNonLorTypes = allFileTypeTitles.some(title => !isLorFileType(title));
                        
                        return hasNonLorTypes || allFileTypeTitles.length === 0;
                    });

                    if (filteredFiles.length === 0) {
                        return (
                            <div className="text-muted">
                                <small>Henüz sigorta başvurusu yapılabilecek dosya bulunmamaktadır. (Sadece Hak Mahrumiyeti içeren dosyalar için sigorta başvurusu yapılamaz.)</small>
                            </div>
                        );
                    }

                    return filteredFiles.map((accidentFileEntry, index) => {
                    const matchedAccidentFile = accidentFileEntry.accidentFileId
                        ? formData.accidentFiles?.find(af => af.id === accidentFileEntry.accidentFileId)
                        : null;
                    const accidentFileId = accidentFileEntry.accidentFileId || index.toString();
                    const fileNumber = accidentFileEntry.fileNumber || (
                        matchedAccidentFile ? matchedAccidentFile.fileNumber : (accidentFileEntry.accidentFileId || 'Yeni')
                    );

                    let allFileTypeTitles = [];
                    
                    if (accidentFileEntry.fileType && !accidentFileEntry.filesToBeWorked) {
                        allFileTypeTitles = accidentFileEntry._fileTypeTitles || [];
                    } else {
                        accidentFileEntry.filesToBeWorked?.forEach(file => {
                            if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                                allFileTypeTitles.push(...file._fileTypeTitles);
                            }
                        });
                    }

                    const isReapplicationFile = accidentFileEntry.isReapplicationFile === true || matchedAccidentFile?.isReapplicationFile === true;
                    const isFromInsurance = accidentFileEntry.isFromInsurance === true || matchedAccidentFile?.isFromInsurance === true;

                    let entryCanEdit = canEdit;
                    if (isInsuranceOnlyWorkflow) {
                        entryCanEdit = entryCanEdit && isFromInsurance;
                    } else if (requiresReapplicationFlag) {
                        entryCanEdit = entryCanEdit && isReapplicationFile;
                    }

                    const currentApplicationData = insuranceApplicationData[accidentFileId] || { 
                        insuranceApplicationDate: accidentFileEntry.insuranceApplicationDate ? 
                            formatDateTimeForInput(accidentFileEntry.insuranceApplicationDate) : '', 
                        insuranceApplicationType: accidentFileEntry.insuranceApplicationType || '' 
                    };

                    return (
                        <div key={accidentFileId} style={{ marginBottom: '30px', border: 'none', boxShadow: 'none' }}>
                            <h6 className="mb-3">
                                Dosya No: {fileNumber} 
                                {allFileTypeTitles.length > 0 && (
                                    <small className="text-muted"> ({allFileTypeTitles.join(', ')})</small>
                                )}
                                {accidentFileEntry.readyForArbitration ? (
                                    <span className="badge rounded-pill bg-success ms-2">Tahkime Hazır</span>
                                ) : (
                                    <span className="badge rounded-pill bg-warning ms-2">Tahkime Hazır Değil</span>
                                )}
                            </h6>
                            
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label for={`insuranceApplicationDate_${accidentFileId}`}>
                                            Sigorta Başvuru Tarihi
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id={`insuranceApplicationDate_${accidentFileId}`}
                                            type="datetime-local"
                                             value={currentApplicationData.insuranceApplicationDate}
                                             onChange={(e) => handleInsuranceApplicationChange(accidentFileId, 'insuranceApplicationDate', e.target.value)}
                                             disabled={!entryCanEdit}
                                             invalid={!!insuranceApplicationErrors[accidentFileId]?.insuranceApplicationDate}
                                         />
                                         <FormFeedback>{insuranceApplicationErrors[accidentFileId]?.insuranceApplicationDate}</FormFeedback>
                                     </FormGroup>
                                 </Col>
                                
                                <Col md={4}>
                                    <FormGroup>
                                        <Label for={`insuranceApplicationType_${accidentFileId}`}>
                                            Sigorta Başvuru Türü
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id={`insuranceApplicationType_${accidentFileId}`}
                                            type="select"
                                             value={currentApplicationData.insuranceApplicationType}
                                             onChange={(e) => handleInsuranceApplicationChange(accidentFileId, 'insuranceApplicationType', e.target.value)}
                                             disabled={!entryCanEdit}
                                             invalid={!!insuranceApplicationErrors[accidentFileId]?.insuranceApplicationType}
                                         >
                                            <option value="">Başvuru türü seçiniz...</option>
                                            {INSURANCE_APPLICATION_TYPES.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                             ))}
                                         </Input>
                                         <FormFeedback>{insuranceApplicationErrors[accidentFileId]?.insuranceApplicationType}</FormFeedback>
                                     </FormGroup>
                                 </Col>
                                
                                <Col md={4} className="d-flex align-items-end">
                                    <FormGroup className="mb-0 w-100">
                                        <div className="d-flex flex-wrap justify-content-end" style={{ gap: '0.5rem' }}>
                                            <Button 
                                                color="success" 
                                                onClick={() => handleSaveInsuranceApplication(accidentFileId)}
                                                disabled={
                                                    !entryCanEdit || 
                                                    !currentApplicationData.insuranceApplicationDate || 
                                                    !currentApplicationData.insuranceApplicationType ||
                                                    savingApplicationData[accidentFileId]
                                                }
                                            >
                                                {savingApplicationData[accidentFileId] ? 'Kaydediliyor...' : 'Kaydet'}
                                            </Button>
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    );
                });
                })()
            ) : (
                <div className="text-muted">
                    <small>Henüz sigorta başvurusu yapılabilecek dosya bulunmamaktadır. (Sadece Hak Mahrumiyeti içeren dosyalar için sigorta başvurusu yapılamaz.)</small>
                </div>
            )}
        </>
    );
};

export default InsuranceCompanyReportForm;
